import type { LucideIcon } from "lucide-react";
import {
  Activity,
  AlertTriangle,
  BarChart3,
  Box,
  Boxes,
  Building2,
  Camera,
  ClipboardList,
  Cpu,
  Grid3x3,
  KeyRound,
  Layers,
  LayoutDashboard,
  LayoutGrid,
  Map,
  MapPin,
  MapPinned,
  Package,
  ScanLine,
  ScrollText,
  Settings,
  SquareDashed,
  Store,
  Tags,
  User,
  Users,
  Webhook,
} from "lucide-react";

export interface NavItem {
  label: string;
  icon: LucideIcon;
  to: string;
  /** When true, only tenant admins see this item (e.g. the audit trail). */
  adminOnly?: boolean;
}

export interface NavSection {
  /** Display name; also the heading of the second-level panel. */
  group: string;
  /** Level-1 rail icon for this group. */
  icon: LucideIcon;
  items: NavItem[];
}

/**
 * The `to` of the single nav item that best matches `pathname` — the longest
 * `to` that is either an exact match or a path-segment prefix. Returns null
 * when nothing matches. Using the most-specific match avoids highlighting both
 * a parent ("/scans") and its child ("/scans/new") at the same time.
 */
export function activeNavTo(pathname: string, items: readonly NavItem[]): string | null {
  let best: string | null = null;
  for (const item of items) {
    const matches = pathname === item.to || pathname.startsWith(`${item.to}/`);
    if (matches && (best === null || item.to.length > best.length)) {
      best = item.to;
    }
  }
  return best;
}

/** Single source of truth for the sidebar nav. 4C appends entries here. */
export const navSections: NavSection[] = [
  {
    group: "Dashboard",
    icon: LayoutDashboard,
    items: [{ label: "Dashboard", icon: LayoutDashboard, to: "/dashboard" }],
  },
  {
    group: "Store Layout",
    icon: Map,
    items: [
      { label: "Overview", icon: LayoutGrid, to: "/store-layout" },
      { label: "Maps", icon: Map, to: "/store-layout/maps" },
      { label: "Active map", icon: MapPinned, to: "/store-layout/map" },
      { label: "3D store", icon: Box, to: "/store-layout/3d" },
      { label: "Zones", icon: SquareDashed, to: "/store-layout/zones" },
      { label: "Fixtures", icon: Boxes, to: "/store-layout/fixtures" },
      { label: "Shelves", icon: Layers, to: "/store-layout/shelves" },
    ],
  },
  {
    group: "Catalog",
    icon: Package,
    items: [
      { label: "Products", icon: Package, to: "/products" },
      { label: "Categories", icon: Tags, to: "/products/categories" },
      { label: "Brands", icon: Tags, to: "/products/brands" },
    ],
  },
  {
    group: "Planograms",
    icon: Grid3x3,
    items: [
      { label: "Planograms", icon: Grid3x3, to: "/planograms" },
      { label: "Assignments", icon: MapPin, to: "/planogram-assignments" },
    ],
  },
  {
    group: "Capture",
    icon: Camera,
    items: [
      { label: "Scans", icon: Camera, to: "/scans" },
      { label: "New scan", icon: ScanLine, to: "/scans/new" },
    ],
  },
  {
    group: "Operations",
    icon: Activity,
    items: [
      { label: "Issues", icon: AlertTriangle, to: "/compliance" },
      { label: "Store tasks", icon: ClipboardList, to: "/compliance/store-tasks" },
      { label: "Analytics", icon: BarChart3, to: "/analytics" },
      { label: "Brand analytics", icon: Tags, to: "/analytics/brands" },
      { label: "Category analytics", icon: LayoutGrid, to: "/analytics/categories" },
      { label: "Location analytics", icon: MapPin, to: "/analytics/locations" },
      { label: "Store map analytics", icon: MapPin, to: "/analytics/map" },
      { label: "Audit Trail", icon: ScrollText, to: "/audit", adminOnly: true },
    ],
  },
  {
    group: "Settings",
    icon: Settings,
    items: [
      { label: "Company", icon: Building2, to: "/settings/company" },
      { label: "Stores", icon: Store, to: "/settings/stores" },
      { label: "Devices", icon: Cpu, to: "/settings/devices" },
      { label: "Webhooks", icon: Webhook, to: "/settings/webhooks" },
      { label: "API keys", icon: KeyRound, to: "/settings/api-keys" },
      { label: "Account", icon: User, to: "/settings/account" },
      { label: "Sessions", icon: KeyRound, to: "/settings/sessions" },
      { label: "Team", icon: Users, to: "/settings/users" },
    ],
  },
];

/**
 * navSections with admin-only items removed for non-admins (and any section
 * left empty dropped). Both the desktop rail and the mobile drawer use this so
 * a viewer/ops user never sees the audit-trail entry.
 */
export function visibleNavSections(isAdmin: boolean): NavSection[] {
  if (isAdmin) return navSections;
  return navSections
    .map((section) => ({ ...section, items: section.items.filter((i) => !i.adminOnly) }))
    .filter((section) => section.items.length > 0);
}
